/**
 * Leases on the shared Arcade game servers (GMs).
 *
 * The gateway starts a game's GM on demand and stops it when nobody uses it
 * (general_services/gm_lifecycle.py). A page that plays a game by hand holds
 * one lease per game while it has a live GM session:
 *
 *   create*Session()  → await acquireGmLease(gameId)   ("Starting game…")
 *                       then POST /session/create
 *   every GM response → noteGmState(gameId, data)      (game over ends the session)
 *   delete*Session()  → endGmSession(gameId, sessionId)
 *   no live sessions  → release (debounced), page hide → sendBeacon release
 *
 * While held, the lease is renewed by a heartbeat (interval from the gateway,
 * 20 s by default); a closed tab's lease simply expires. All GM clients
 * (Games/*GmClient.js) and the Two-Joint Pole multiplayer page go through
 * this module, and pages read its status through useGmLease().
 */
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/config';

/** Arcade game ids that use another game's GM (game_engines/gm_registry.py GM_ALIASES). */
export const GM_ALIASES = {
    two_joint_pole_multiplayer: 'two_joint_pole',
};

export const GM_START_ERROR = "Couldn't start the game server. Please try again in a moment.";
export const GM_STARTING_MESSAGE = 'Starting game…';

const DEFAULT_HEARTBEAT_MS = 20000;
const RELEASE_DEBOUNCE_MS = 3000;
// Stop renewing a lease after this long without any GM traffic from the page
// (an abandoned but open tab must not keep a game server running forever).
const IDLE_RELEASE_MS = 10 * 60 * 1000;

const games = new Map();

export function gmIdFor(gameId) {
    return GM_ALIASES[gameId] || gameId;
}

function stateFor(gameId) {
    const id = gmIdFor(gameId);
    let st = games.get(id);
    if (!st) {
        st = {
            id,
            status: 'idle',
            error: null,
            leaseId: null,
            heartbeatMs: DEFAULT_HEARTBEAT_MS,
            heartbeatTimer: null,
            releaseTimer: null,
            acquiring: null,
            sessions: new Set(),
            // Sessions ended by End / delete / game over: ignore late /move
            // responses so they cannot re-track the session and cancel release
            // (or re-acquire after the page already released).
            endedSessions: new Set(),
            lastActivity: 0,
            mounts: 0,
            listeners: new Set(),
        };
        games.set(id, st);
    }
    return st;
}

function emit(st) {
    const snap = { status: st.status, error: st.error };
    st.listeners.forEach((fn) => {
        try {
            fn(snap);
        } catch {
            /* listener errors must not break the lease */
        }
    });
}

function setStatus(st, status, error = null) {
    if (st.status === status && st.error === error) return;
    st.status = status;
    st.error = error;
    emit(st);
}

function leaseUrl(id, action, leaseId) {
    const base = `${API_BASE_URL}/api/gm/${encodeURIComponent(id)}/${action}`;
    return leaseId ? `${base}?lease_id=${encodeURIComponent(leaseId)}` : base;
}

/** Error thrown by acquireGmLease; `message` is safe to show to the player. */
export class GmStartError extends Error {
    constructor(message, status) {
        super(message || GM_START_ERROR);
        this.name = 'GmStartError';
        this.status = status;
        this.isGmStartError = true;
    }
}

function startErrorMessage(status, data) {
    // 429 (rate limit) and 503 (all servers busy) carry a player-facing message.
    if ((status === 429 || status === 503) && data && typeof data.error === 'string') {
        return data.error;
    }
    if (status === 404) return "This game isn't available right now.";
    return GM_START_ERROR;
}

function clearTimers(st) {
    if (st.heartbeatTimer) clearInterval(st.heartbeatTimer);
    if (st.releaseTimer) clearTimeout(st.releaseTimer);
    st.heartbeatTimer = null;
    st.releaseTimer = null;
}

function startHeartbeat(st) {
    if (st.heartbeatTimer) clearInterval(st.heartbeatTimer);
    st.heartbeatTimer = setInterval(() => {
        heartbeatOnce(st);
    }, st.heartbeatMs);
}

async function heartbeatOnce(st) {
    const leaseId = st.leaseId;
    if (!leaseId) return;
    if (Date.now() - st.lastActivity > IDLE_RELEASE_MS) {
        releaseGmLease(st.id);
        return;
    }
    try {
        const res = await fetch(leaseUrl(st.id, 'heartbeat'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lease_id: leaseId }),
        });
        if (res.status === 404 && st.leaseId === leaseId) {
            // The gateway forgot the lease (expired or restarted): get a new one.
            st.leaseId = null;
            if (st.sessions.size > 0) {
                acquireGmLease(st.id).catch(() => {});
            } else {
                clearTimers(st);
                setStatus(st, 'idle');
            }
        }
    } catch {
        /* a missed heartbeat is fine; the lease outlives a few of them */
    }
}

/**
 * Make sure this page holds a lease on the game's GM (starting the GM if
 * needed). Resolves with the lease id once the GM passed its health check;
 * rejects with a GmStartError carrying a player-facing message.
 */
export function acquireGmLease(gameId) {
    const st = stateFor(gameId);
    st.lastActivity = Date.now();
    if (st.releaseTimer) {
        clearTimeout(st.releaseTimer);
        st.releaseTimer = null;
    }
    if (st.leaseId) return Promise.resolve(st.leaseId);
    if (st.acquiring) return st.acquiring;
    setStatus(st, 'starting');
    st.acquiring = (async () => {
        let res;
        let data = {};
        try {
            res = await fetch(leaseUrl(st.id, 'acquire'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kind: 'manual' }),
            });
            data = await res.json().catch(() => ({}));
        } catch {
            const err = new GmStartError(GM_START_ERROR, 0);
            setStatus(st, 'error', err.message);
            throw err;
        }
        if (!res.ok || !data || !data.lease_id) {
            const err = new GmStartError(startErrorMessage(res.status, data), res.status);
            setStatus(st, 'error', err.message);
            throw err;
        }
        st.leaseId = data.lease_id;
        const hb = Number(data.heartbeat_s);
        st.heartbeatMs = Number.isFinite(hb) && hb > 0 ? hb * 1000 : DEFAULT_HEARTBEAT_MS;
        startHeartbeat(st);
        setStatus(st, 'ready');
        return st.leaseId;
    })();
    st.acquiring.then(
        () => { st.acquiring = null; },
        () => { st.acquiring = null; },
    );
    return st.acquiring;
}

/**
 * Give the lease back now. `beacon` uses navigator.sendBeacon so it still
 * goes out while the page is being hidden or unloaded.
 */
export function releaseGmLease(gameId, { beacon = false } = {}) {
    const st = stateFor(gameId);
    const leaseId = st.leaseId;
    clearTimers(st);
    st.leaseId = null;
    st.sessions.clear();
    // Keep endedSessions: a late /move after page leave must still see the
    // session as ended, or trackGmSession would re-acquire the lease.
    if (st.status !== 'error') setStatus(st, 'idle');
    if (!leaseId) return;
    const url = leaseUrl(st.id, 'release', leaseId);
    if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
            if (navigator.sendBeacon(url, '')) return;
        } catch {
            /* fall through to fetch */
        }
    }
    try {
        const p = fetch(url, { method: 'POST', keepalive: true });
        if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
        /* best-effort: the lease expires on its own */
    }
}

function scheduleRelease(st) {
    if (st.releaseTimer) clearTimeout(st.releaseTimer);
    // Debounced: "New game" deletes the old session and creates the next one.
    st.releaseTimer = setTimeout(() => {
        st.releaseTimer = null;
        if (st.sessions.size === 0) releaseGmLease(st.id);
    }, RELEASE_DEBOUNCE_MS);
}

export function trackGmSession(gameId, sessionId) {
    if (!sessionId) return;
    const st = stateFor(gameId);
    const sid = String(sessionId);
    // Late /move after End or page leave must not revive an ended session.
    if (st.endedSessions.has(sid)) return;
    st.lastActivity = Date.now();
    st.sessions.add(sid);
    if (st.releaseTimer) {
        clearTimeout(st.releaseTimer);
        st.releaseTimer = null;
    }
    if (!st.leaseId && !st.acquiring) {
        // A session continued after its lease was released (e.g. reset after game over).
        acquireGmLease(st.id).catch(() => {});
    }
}

/** End Game / delete / game over: the session no longer needs the GM. */
export function endGmSession(gameId, sessionId) {
    const st = stateFor(gameId);
    if (sessionId) {
        const sid = String(sessionId);
        st.sessions.delete(sid);
        st.endedSessions.add(sid);
    }
    if (st.sessions.size === 0 && (st.leaseId || st.acquiring)) scheduleRelease(st);
}

export function isGmGameOver(data) {
    if (!data || typeof data !== 'object') return false;
    return data.is_game_over === true
        || data.game_over === true
        || data.is_finished === true
        || data.is_game_won === true;
}

/**
 * Feed every GM session response through here (the GM clients do): keeps the
 * lease alive while a session is in play and ends it on game over.
 * Responses for a session that already ended (End / delete) are ignored by
 * trackGmSession so an in-flight /move cannot cancel the release.
 */
export function noteGmState(gameId, data) {
    const st = stateFor(gameId);
    st.lastActivity = Date.now();
    const sid = data && typeof data === 'object' ? data.session_id : null;
    if (!sid) return data;
    if (isGmGameOver(data)) {
        endGmSession(gameId, sid);
    } else {
        trackGmSession(gameId, sid);
    }
    return data;
}

/** Player-facing message for an error thrown while starting a game. */
export function gmStartErrorMessage(err) {
    if (err && err.isGmStartError && err.message) return err.message;
    return GM_START_ERROR;
}

export function getGmLeaseState(gameId) {
    const st = stateFor(gameId);
    return { status: st.status, error: st.error };
}

export function subscribeGmLease(gameId, fn) {
    const st = stateFor(gameId);
    st.listeners.add(fn);
    return () => st.listeners.delete(fn);
}

function releaseAllWithBeacon() {
    games.forEach((st) => {
        if (st.leaseId) releaseGmLease(st.id, { beacon: true });
    });
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    // pagehide covers tab close, navigation away and bfcache; beforeunload
    // alone misses mobile browsers.
    window.addEventListener('pagehide', releaseAllWithBeacon);
}

/**
 * React view of a game's lease: { status: 'idle'|'starting'|'ready'|'error',
 * error, starting }. The lease is released when the last component using
 * the hook for that game unmounts (leaving the game page).
 */
export function useGmLease(gameId) {
    const [snap, setSnap] = useState(() => (gameId ? getGmLeaseState(gameId) : { status: 'idle', error: null }));
    useEffect(() => {
        if (!gameId) return undefined;
        const st = stateFor(gameId);
        st.mounts += 1;
        setSnap(getGmLeaseState(gameId));
        const unsubscribe = subscribeGmLease(gameId, setSnap);
        return () => {
            unsubscribe();
            st.mounts -= 1;
            if (st.mounts <= 0) {
                st.mounts = 0;
                releaseGmLease(gameId);
                setStatus(st, 'idle');
            }
        };
    }, [gameId]);
    return { ...snap, starting: snap.status === 'starting' };
}

/** Test helper: forget all leases and timers. */
export function resetGmLeasesForTests() {
    games.forEach((st) => clearTimers(st));
    games.clear();
}
